// The play economy (BUILD_PLAN_NEXT §4.2, provisional: owner to confirm). One pool of plays per
// player per play-day, spendable on any machine:
//   · a free play or two each day (rules.free), use it or lose it;
//   · earned plays (the daily Learn goal, check-ins, activities a grown-up confirms) go into a
//     bank that rolls over, but at most `cap` plays (the child's "most a day") can be used a day,
//     free ones included, and the bank never holds more than `cap`;
//   · a grown-up's bonus (plays.granted) is on top of the cap, for that day only.
// Pure: every phone folds the same events to the same numbers. All of the rules live here, so
// changing them is one file.
import { RewardAwarded, RewardEarned, TaskDone, type RewardRules } from '../../../shared/rewards'
import { taskPool } from '../tasks'
import type { ArcadeEvent } from './progress'

export type PlayTrigger = 'free' | 'daily' | 'checkin' | 'activity' | 'task' | 'bonus'

export interface PlayLine {
  trigger: PlayTrigger
  /** What it was for: the day, the place checked in at, the plan item. */
  ref: string
  plays: number
}

export interface DayPlays {
  day: string
  cap: number
  /** Plays brought in from earlier days. */
  carried: number
  /** How today's plays came (r03), in order. */
  lines: PlayLine[]
  used: number
  left: number
  /** Earned today but over the cap: they wait for tomorrow. */
  waiting: number
}

interface DayIn {
  earned: PlayLine[]
  bonus: number
  used: number
}

/** Today's pool for `playerId`. `today` is the play-day (domain/arcade/playDay). */
export function playsToday(events: readonly ArcadeEvent[], playerId: string, today: string, cap: number, rules: RewardRules): DayPlays {
  const days = new Map<string, DayIn>()
  const dayOf = (d: string) => {
    let x = days.get(d)
    if (!x) days.set(d, (x = { earned: [], bonus: 0, used: 0 }))
    return x
  }
  const counted = new Set<string>()
  const earn = (day: string, line: PlayLine) => {
    const key = `${line.trigger}|${line.ref}`
    if (counted.has(key)) return // a rule pays once per day / place / plan
    counted.add(key)
    dayOf(day).earned.push(line)
  }

  const sorted = [...events].sort((a, b) => a.at - b.at || (a.id < b.id ? -1 : 1))
  // Holiday tasks (WI-13b) pay what the parent's list says, once each (self-ticked or confirmed).
  const tasksOn = rules.tasks?.on !== false
  const taskPlays = tasksOn ? new Map(taskPool(events).map((t) => [t.id, t.plays])) : new Map<string, number>()
  for (const e of sorted) {
    if (e.type === 'task.done') {
      const p = TaskDone.safeParse(e.payload)
      const n = p.success ? taskPlays.get(p.data.taskId) : undefined
      if (p.success && n && p.data.for === playerId) earn(p.data.day, { trigger: 'task', ref: p.data.taskId, plays: n })
    } else if (e.type === 'plays.granted') {
      const g = e.payload as { for?: string; n?: number; day?: string }
      if (g.for === playerId && g.day && g.n) dayOf(g.day).bonus += g.n
    } else if (e.type === 'reward.awarded') {
      const p = RewardAwarded.safeParse(e.payload)
      if (p.success && p.data.for === playerId) earn(p.data.day, { trigger: 'activity', ref: p.data.itemId, plays: p.data.plays })
    } else if (e.personId !== playerId) {
      continue
    } else if (e.type === 'reward.earned') {
      const p = RewardEarned.safeParse(e.payload)
      if (p.success) earn(p.data.day, { trigger: p.data.trigger, ref: p.data.ref, plays: p.data.plays })
    } else if (e.type === 'session.started') {
      const d = (e.payload as { day?: string }).day
      if (d) dayOf(d).used++
    }
  }

  const free = rules.free?.plays ?? 0
  let bank = 0
  let out: DayPlays | null = null
  for (const day of [...new Set([...days.keys(), today])].filter((d) => d <= today).sort()) {
    const d = days.get(day) ?? { earned: [], bonus: 0, used: 0 }
    const carried = bank
    const pot = bank + d.earned.reduce((s, l) => s + l.plays, 0)
    const freeToday = Math.min(free, cap)
    const potToday = Math.min(pot, cap - freeToday)
    // Spend the free plays first, then the grown-up's bonus, then the bank.
    let rest = d.used
    const usedFree = Math.min(rest, freeToday)
    rest -= usedFree
    const usedBonus = Math.min(rest, d.bonus)
    rest -= usedBonus
    const usedPot = Math.min(rest, potToday)
    bank = Math.min(cap, pot - usedPot)
    if (day === today) {
      out = {
        day,
        cap,
        carried,
        lines: [...(freeToday ? [{ trigger: 'free' as const, ref: day, plays: freeToday }] : []), ...d.earned, ...(d.bonus ? [{ trigger: 'bonus' as const, ref: day, plays: d.bonus }] : [])],
        used: d.used,
        left: Math.max(0, freeToday + d.bonus + potToday - d.used),
        waiting: Math.max(0, pot - potToday),
      }
    }
  }
  return out!
}

/**
 * Right answers a player gave on a play-day: what the daily goal counts (Learn and the Arcade
 * both count, §4.3). Only `right` (a right choice; phrases are a choice too since 23 Oct): a
 * wrong answer, a skip or an old self-rated "close" doesn't move the goal (WI-08; it used to
 * count every answer, so tapping anything, or Skip, reached the goal).
 */
export function rightOn(events: readonly ArcadeEvent[], playerId: string, day: string): number {
  return events.filter((e) => {
    if (e.type !== 'card.answered' || e.personId !== playerId) return false
    const p = e.payload as { day?: string; result?: string }
    return p.day === day && p.result === 'right'
  }).length
}

/** The daily goal was met today and hasn't paid yet: the phone should record `reward.earned`. */
export function dailyGoalDue(events: readonly ArcadeEvent[], playerId: string, day: string, goal: number, rules: RewardRules): boolean {
  if (!rules.daily.on || rightOn(events, playerId, day) < goal) return false
  return !events.some((e) => e.type === 'reward.earned' && e.personId === playerId && (e.payload as { trigger?: string; ref?: string }).trigger === 'daily' && (e.payload as { ref?: string }).ref === day)
}
